import { Locale } from './intl';

// const specialThousandSeparators = ["'", '’', '`'];
// const specialThousandSeparatorRegex = /['’`]/g;
export const containsValidNumberCharacters = (text: string) => {
  if (text) {
    const reg = RegExp(/^[0-9,.\-–'’`]+$/g);
    return reg.test(text);
  } else return true;
};

const isMinus = (text: string) => {
  return text === '-' || text === '–';
};

const isSpecialZero = (text: string) => {
  if (text.endsWith(',--') || text.endsWith('.--') || text.endsWith(',––') || text.endsWith('.––'))
    return true;
  else if (text.endsWith(',-') || text.endsWith('.-') || text.endsWith(',–') || text.endsWith('.–'))
    return true;
  else return false;
};

const prepareText = (text: string) => {
  if (!text) return text;
  let textCopy = text;
  textCopy = textCopy.replace(' ', '');
  textCopy = textCopy.replace('O', '0');
  textCopy = textCopy.replace('o', '0');
  if (
    textCopy.endsWith(',--') ||
    textCopy.endsWith('.--') ||
    textCopy.endsWith(',––') ||
    textCopy.endsWith('.––')
  )
    textCopy = textCopy.substr(0, textCopy.length - 2) + '00';
  if (
    textCopy.endsWith(',-') ||
    textCopy.endsWith('.-') ||
    textCopy.endsWith(',–') ||
    textCopy.endsWith('.–')
  )
    textCopy = textCopy.substr(0, textCopy.length - 1) + '00';
  if (textCopy.endsWith('-') || textCopy.endsWith('–')) {
    const textCopySplit = textCopy.split('');
    textCopySplit[0] = textCopySplit[textCopySplit.length - 1];
    textCopySplit.pop();
    textCopy = textCopySplit.join('');
  }
  textCopy = textCopy.replace(/[^0-9,.\-–'’`]/g, '');
  return textCopy;
};

const findDecimalSeparator = (text: string) => {
  if (text[text.length - 2] === '.' || text[text.length - 2] === ',') {
    return text.length - 2;
  }
  if (text[text.length - 3] === '.' || text[text.length - 3] === ',') {
    return text.length - 3;
  }
  return -1;
};

const replaceDecimalSeparator = (text: string, location: number, newDecimalSeparator: string) => {
  const textSplit = text.split('');
  textSplit[location] = newDecimalSeparator;
  return textSplit.join('');
};

const replaceThousandSeparator = (
  text: string,
  newThousandSeparator: string,
  decimalSeparator: string | undefined = undefined,
) => {
  return text
    .split('')
    .map((val) => {
      const numberRegEx = /[0-9]/g;
      const isNotNumber = !numberRegEx.test(val);
      if (!decimalSeparator && isNotNumber) return newThousandSeparator;
      if (isNotNumber && val !== decimalSeparator) return newThousandSeparator;
      else return val;
    })
    .join('');
};

export const convertToEuropeanStandard = (text: string) => {
  if (!text) return text;
  let textCopy = text;
  let negative = false;

  if (!isSpecialZero(textCopy) && isMinus(textCopy[textCopy.length - 1])) {
    negative = true;
    textCopy = textCopy.slice(0, textCopy.length - 1);
  }

  if (isMinus(textCopy[0])) {
    negative = true;
    textCopy = textCopy.slice(1);
  }
  textCopy = prepareText(textCopy);
  const decimalSeparatorLocation = findDecimalSeparator(textCopy);
  if (decimalSeparatorLocation !== -1) {
    textCopy = replaceThousandSeparator(textCopy, '.', textCopy[decimalSeparatorLocation]);
    textCopy = replaceDecimalSeparator(textCopy, decimalSeparatorLocation, ',');
  } else textCopy = replaceThousandSeparator(textCopy, '.');

  return negative ? `-${textCopy}` : textCopy;
};

export const convertToUSStandard = (text: string) => {
  if (!text) return text;
  let textCopy = text;
  let negative = false;

  if (!isSpecialZero(textCopy) && isMinus(textCopy[textCopy.length - 1])) {
    negative = true;
    textCopy = textCopy.slice(0, textCopy.length - 1);
  }

  if (isMinus(textCopy[0])) {
    negative = true;
    textCopy = textCopy.slice(1);
  }
  textCopy = prepareText(textCopy);
  const decimalSeparatorLocation = findDecimalSeparator(textCopy);
  if (decimalSeparatorLocation !== -1) {
    textCopy = replaceThousandSeparator(textCopy, ',', textCopy[decimalSeparatorLocation]);
    textCopy = replaceDecimalSeparator(textCopy, decimalSeparatorLocation, '.');
  } else textCopy = replaceThousandSeparator(textCopy, ',');

  return negative ? `-${textCopy}` : textCopy;
};

export const convertToLocale = (text: string, locale: Locale) => {
  if (locale === 'de') return convertToEuropeanStandard(text);
  else return convertToUSStandard(text);
};

const isNumber = (text: string) => {
  return new RegExp(/[0-9]/g).test(text);
};

const findFirstNonNumericCharacter = (text: string) => {
  for (let i = 0; i < text.length; i++) {
    const element = text[i];
    if (!isNumber(element)) return i;
  }
  return -1;
};

const isValidSeparator = (text: string) => {
  //Checks if the character at the beggining of text is a valid separator
  //the first character of text needs to be a non number character
  //the function returns: 0->valid thousand separator; 1->valid decimal separator; 2->invalid separator
  if (text.length === 1) return 2;
  let i;
  for (i = 1; i < text.length && i < 4; i++) {
    const element = text[i];
    if (!isNumber(element)) return 2;
  }
  i--;
  if (i < 3) {
    return 1;
  }
  if (!isNumber(text[4])) return 0;
  return 2;
};

export const validateNumber = (text: string) => {
  if (!text) return true;
  const thousandSeparators = new Map<string, boolean>();
  let textCopy = text;

  if (isMinus(textCopy[0])) textCopy = textCopy.slice(1);
  if (textCopy.includes('-') || textCopy.includes('–')) return false;
  const nonNumberCharPosition = findFirstNonNumericCharacter(textCopy);
  if (nonNumberCharPosition === -1) return true;
  if (textCopy.length === nonNumberCharPosition + 1) return false;
  //first non number char position is >=4 meaning that a decimal separator is allowed
  if (nonNumberCharPosition >= 4) {
    for (
      let i = nonNumberCharPosition + 1;
      i < textCopy.length && i < nonNumberCharPosition + 4;
      i++
    ) {
      const element = textCopy[i];
      if (i === nonNumberCharPosition + 3) return false;
      if (!isNumber(element)) return false;
    }
    return !new RegExp(/['’`]/g).test(textCopy[nonNumberCharPosition]);
  }
  //after the first separator the next 3 or less character have to be numbers
  let iterator;
  for (
    iterator = nonNumberCharPosition + 1;
    iterator < textCopy.length && iterator < nonNumberCharPosition + 4;
    iterator++
  ) {
    const element = textCopy[iterator];
    if (!isNumber(element)) return false;
  }
  iterator--;
  //if we get here it means that the separator is a decimal separator and we got to the end of the string, so we should check if it's a valid decimal separator character
  if (iterator < nonNumberCharPosition + 3)
    // ' or ’ or `  are swiss thousand separators and cannot appear as decimal separators
    return !new RegExp(/['’`]/g).test(textCopy[nonNumberCharPosition]);
  //we didn't get to the end of the string so the separator was a valid thousand separator, so we add it to the map
  //the map is used to check if there are multiple thousand separators, which is not valid.
  thousandSeparators.set(textCopy[nonNumberCharPosition], true);
  textCopy = textCopy.slice(nonNumberCharPosition + 1);
  //here we check the rest of the separators in the text
  while (textCopy) {
    //find the next separator
    const nextNonNumberCharPosition = findFirstNonNumericCharacter(textCopy);
    //if we cant find one it means that only thousand separators appear in the text
    //so we should only have 3 numbers after every separator
    if (nextNonNumberCharPosition === -1) {
      if (textCopy.length === 3) return true;
    }
    //we take the part of the string where the first character is the separator and check if it's a valid separator
    textCopy = textCopy.slice(nextNonNumberCharPosition);
    const result = isValidSeparator(textCopy);
    if (result === 2) return false;
    //if the character is a valid decimal separator check if it didn't appear as a thousand separator and it's not a special thousand separator
    if (result === 1) {
      return !thousandSeparators.has(textCopy[0]) && !new RegExp(/['’`]/g).test(textCopy[0]);
    } else {
      // thousandSeparators[textCopy[nextNonNumberCharPosition]] = true;
      //if the character is a thousand separator add it to the map
      thousandSeparators.set(textCopy[0], true);
      //check if it's the only thousand separator, and if it is continue the loop
      if (thousandSeparators.size > 1) return false;
      textCopy = textCopy.slice(nextNonNumberCharPosition + 1);
    }
  }
  return true;
};
